"use client"

import { useEffect, useMemo, useState } from "react"
import {
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Checkbox,
} from "@material-ui/core"
import Alert from "@material-ui/lab/Alert"
import {
  Clear as ClearIcon,
  CloudUpload as UploadIcon,
  GetApp as DownloadIcon,
  Search as SearchIcon,
} from "@material-ui/icons"
import firebase from "../firebase"
import Navbar from "./Navbar"
import {
  isValidForCodesImport,
  resolveVolumeSize,
  VOLUME_SIZE_OPTIONS,
} from "../utils/volumeDetector"

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL
const MAGICBOX_API_KEY = process.env.REACT_APP_MAGICBOX_API_KEY

const IMPORT_CAMPAIGN_ID = 5
const IMPORT_PEDIDO = 0
const IMPORT_BRAND = " "

function csvEscape(value) {
  const s = value === null || value === undefined ? "" : String(value)
  const needsQuotes = /[",\n\r]/.test(s)
  const escaped = s.replace(/"/g, '""')
  return needsQuotes ? `"${escaped}"` : escaped
}

function truncateText(text, maxLength = 40) {
  if (!text) return ""
  if (text.length <= maxLength) return text
  return `${text.substring(0, maxLength)}...`
}

function QrExport() {
  const [qrCodes, setQrCodes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [searchTerm, setSearchTerm] = useState("")
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(50)

  const [selectedIds, setSelectedIds] = useState([])
  const [manualVolumeByCode, setManualVolumeByCode] = useState({})
  const [importing, setImporting] = useState(false)
  const [importSummary, setImportSummary] = useState(null)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState("")
  const [snackbarSeverity, setSnackbarSeverity] = useState("success")

  useEffect(() => {
    try {
      const qrCodesRef = firebase.firestore().collection("qrcodes")
      const unsubscribe = qrCodesRef.onSnapshot(
        (snapshot) => {
          const codes = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
            title: doc.data().title || "Untitled",
            url: doc.data().url || "",
            numericId: doc.data().numericId || 0,
          }))
          setQrCodes(codes)
          setLoading(false)
        },
        (err) => {
          console.error("Error fetching documents: ", err)
          setError(err.message)
          setLoading(false)
        },
      )

      return () => unsubscribe()
    } catch (err) {
      console.error("Error setting up listener: ", err)
      setError(err.message)
      setLoading(false)
    }
  }, [])

  const filteredQrCodes = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    if (!term) return [...qrCodes].sort((a, b) => (b.numericId || 0) - (a.numericId || 0))

    const filtered = qrCodes.filter((qr) => {
      const numericIdMatch = qr.numericId !== null && qr.numericId !== undefined && String(qr.numericId).includes(term)
      const codeMatch = qr.id ? qr.id.toLowerCase().includes(term) : false
      const titleMatch = qr.title ? qr.title.toLowerCase().includes(term) : false
      const urlMatch = qr.url ? qr.url.toLowerCase().includes(term) : false
      return numericIdMatch || codeMatch || titleMatch || urlMatch
    })

    return filtered.sort((a, b) => (b.numericId || 0) - (a.numericId || 0))
  }, [qrCodes, searchTerm])

  const paginatedQrCodes = useMemo(
    () => filteredQrCodes.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredQrCodes, page, rowsPerPage],
  )

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])

  const filteredIds = useMemo(() => filteredQrCodes.map((qr) => qr.id), [filteredQrCodes])

  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedSet.has(id))
  const someFilteredSelected = filteredIds.some((id) => selectedSet.has(id)) && !allFilteredSelected

  const volumeByCode = useMemo(() => {
    const map = new Map()
    for (const qr of qrCodes) {
      map.set(qr.id, resolveVolumeSize(qr.title, manualVolumeByCode[qr.id]))
    }
    return map
  }, [qrCodes, manualVolumeByCode])

  const handleVolumeChange = (codeId, value) => {
    setManualVolumeByCode((prev) => {
      if (!value) {
        const next = { ...prev }
        delete next[codeId]
        return next
      }
      return { ...prev, [codeId]: value }
    })
  }

  const handleToggleRow = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  const handleToggleAllFiltered = (checked) => {
    setSelectedIds((prev) => {
      const prevSet = new Set(prev)
      if (checked) {
        for (const id of filteredIds) prevSet.add(id)
        return Array.from(prevSet)
      }
      for (const id of filteredIds) prevSet.delete(id)
      return Array.from(prevSet)
    })
  }

  const clearSearch = () => setSearchTerm("")

  const exportSelectedToCsv = () => {
    const rows = qrCodes
      .filter((qr) => selectedSet.has(qr.id))
      .sort((a, b) => (a.numericId || 0) - (b.numericId || 0))
      .map((qr) => ({
        numericId: qr.numericId || "",
        code: qr.id || "",
        title: qr.title || "",
        url: qr.url || "",
      }))

    const headers = ["id", "code", "title", "url"]
    const lines = [headers.join(",")]
    for (const r of rows) {
      lines.push([r.numericId, r.code, r.title, r.url].map(csvEscape).join(","))
    }

    const csv = `\uFEFF${lines.join("\n")}\n`
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)

    const a = document.createElement("a")
    a.href = url
    a.download = `qr-export-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  const uploadCodeToApi = async (qr, volumeInfo) => {
    const response = await fetch(`${API_BASE_URL}/codes/upload`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-key": MAGICBOX_API_KEY,
      },
      body: JSON.stringify({
        code: qr.id,
        campaignId: IMPORT_CAMPAIGN_ID,
        pedido: IMPORT_PEDIDO,
        brand: IMPORT_BRAND,
        model: qr.title,
        sizeId: volumeInfo.sizeId,
        pointsToGive: volumeInfo.pointsToGive,
      }),
    })

    let result = {}
    try {
      result = await response.json()
    } catch {
      result = {}
    }

    return { response, result }
  }

  const importSelectedToCodes = async () => {
    if (!MAGICBOX_API_KEY || !API_BASE_URL) {
      setError("La API de MagicBox no está configurada. Revisá REACT_APP_API_BASE_URL y REACT_APP_MAGICBOX_API_KEY.")
      return
    }

    const selected = qrCodes.filter((qr) => selectedSet.has(qr.id))
    if (selected.length === 0) return

    setImporting(true)
    setError(null)
    setImportSummary(null)

    const summary = { insertados: 0, duplicados: 0, ignorados: 0, errores: 0 }

    for (const qr of selected) {
      const volumeInfo = volumeByCode.get(qr.id) || resolveVolumeSize(qr.title, manualVolumeByCode[qr.id])

      if (!isValidForCodesImport(volumeInfo)) {
        summary.errores += 1
        continue
      }

      try {
        const { response, result } = await uploadCodeToApi(qr, volumeInfo)

        if (response.status === 201) {
          summary.insertados += 1
        } else if (response.status === 400) {
          summary.duplicados += 1
        } else if (response.status === 401) {
          setError("API key inválida o faltante")
          setSnackbarMessage("API key inválida o faltante")
          setSnackbarSeverity("error")
          setSnackbarOpen(true)
          setImporting(false)
          return
        } else {
          summary.errores += 1
          console.error(`Error al cargar ${qr.id}:`, result.message || response.status)
        }
      } catch (err) {
        summary.errores += 1
        console.error(`Error al cargar ${qr.id}:`, err)
      }
    }

    setImportSummary(summary)
    setSnackbarMessage(
      `Carga finalizada: ${summary.insertados} insertados, ${summary.duplicados} duplicados, ${summary.errores} con error`,
    )
    setSnackbarSeverity(summary.insertados > 0 ? "success" : "warning")
    setSnackbarOpen(true)
    setImporting(false)
  }

  if (loading) {
    return (
      <>
        <Navbar />
        <Container style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "70vh" }}>
          <CircularProgress />
        </Container>
      </>
    )
  }

  return (
    <>
      <Navbar />
      <Container maxWidth="lg" style={{ paddingTop: "2rem", paddingBottom: "2rem" }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" style={{ gap: "1rem" }}>
          <Box>
            <Typography variant="h4" style={{ fontWeight: 700 }}>
              Export QR list
            </Typography>
            <Typography variant="body2" style={{ color: "#6b7280", marginTop: "0.25rem" }}>
              {filteredQrCodes.length} results • {selectedIds.length} selected
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" flexWrap="wrap" style={{ gap: "0.75rem" }}>
            <Button
              variant="outlined"
              onClick={() => setSelectedIds(filteredIds)}
              disabled={filteredQrCodes.length === 0 || importing}
            >
              Seleccionar todos
            </Button>
            <Button
              variant="outlined"
              onClick={() => setSelectedIds([])}
              disabled={selectedIds.length === 0 || importing}
            >
              Clear selection
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={importing ? <CircularProgress size={18} /> : <UploadIcon />}
              onClick={importSelectedToCodes}
              disabled={selectedIds.length === 0 || importing}
            >
              Cargar en codes
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={exportSelectedToCsv}
              disabled={selectedIds.length === 0 || importing}
            >
              Export selected CSV
            </Button>
          </Box>
        </Box>

        {error && (
          <Paper style={{ padding: "1rem", marginTop: "1rem", border: "1px solid #fecaca", background: "#fff1f2" }}>
            <Typography variant="body2" style={{ color: "#991b1b" }}>
              {error}
            </Typography>
          </Paper>
        )}

        {importSummary && (
          <Paper style={{ padding: "1rem", marginTop: "1rem", border: "1px solid #bbf7d0", background: "#f0fdf4" }}>
            <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: "0.5rem" }}>
              Resumen de carga en codes
            </Typography>
            <Typography variant="body2" style={{ color: "#166534" }}>
              Insertados: {importSummary.insertados} • Duplicados: {importSummary.duplicados} • Con error:{" "}
              {importSummary.errores}
            </Typography>
          </Paper>
        )}

        <Box style={{ marginTop: "1.25rem" }}>
          <TextField
            fullWidth
            variant="outlined"
            className="search-input"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              setPage(0)
            }}
            placeholder="Search by ID, code, title or URL"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "#6366f1" }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton onClick={clearSearch} size="small">
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <TableContainer component={Paper} className="qr-table" style={{ marginTop: "1rem" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allFilteredSelected}
                    indeterminate={someFilteredSelected}
                    onChange={(e) => handleToggleAllFiltered(e.target.checked)}
                    inputProps={{ "aria-label": "select all filtered" }}
                    disabled={importing}
                  />
                </TableCell>
                <TableCell>ID</TableCell>
                <TableCell>Código</TableCell>
                <TableCell>Título</TableCell>
                <TableCell>Tamaño</TableCell>
                <TableCell>Volumen</TableCell>
                <TableCell>sizeId</TableCell>
                <TableCell>Puntos</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>URL</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedQrCodes.length > 0 ? (
                paginatedQrCodes.map((qr) => {
                  const volumeInfo = volumeByCode.get(qr.id) || resolveVolumeSize(qr.title, manualVolumeByCode[qr.id])
                  const isValid = isValidForCodesImport(volumeInfo)
                  const manualValue = manualVolumeByCode[qr.id] ?? ""

                  return (
                    <TableRow key={qr.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedSet.has(qr.id)}
                          onChange={() => handleToggleRow(qr.id)}
                          disabled={importing}
                        />
                      </TableCell>
                      <TableCell>{qr.numericId || "-"}</TableCell>
                      <TableCell title={qr.id} style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>
                        {qr.id || "-"}
                      </TableCell>
                      <TableCell title={qr.title}>{truncateText(qr.title)}</TableCell>
                      <TableCell style={{ minWidth: 160 }}>
                        <FormControl variant="outlined" size="small" fullWidth disabled={importing}>
                          <Select
                            value={manualValue}
                            onChange={(e) => handleVolumeChange(qr.id, e.target.value)}
                            displayEmpty
                          >
                            <MenuItem value="">Auto (título)</MenuItem>
                            {VOLUME_SIZE_OPTIONS.map((option) => (
                              <MenuItem key={option.ml} value={String(option.ml)}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell>
                        {volumeInfo.detectedMl != null ? `${volumeInfo.detectedMl} ml` : "—"}
                      </TableCell>
                      <TableCell>{volumeInfo.sizeId ?? "—"}</TableCell>
                      <TableCell>{volumeInfo.pointsToGive ?? "—"}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          style={{
                            color: isValid ? "#166534" : "#991b1b",
                            fontSize: "0.8rem",
                          }}
                        >
                          {isValid
                            ? volumeInfo.source === "manual"
                              ? "manual"
                              : "detectado"
                            : "sin tamaño"}
                        </Typography>
                      </TableCell>
                      <TableCell title={qr.url}>{truncateText(qr.url, 60)}</TableCell>
                    </TableRow>
                  )
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body1" style={{ padding: "2rem 0" }}>
                      No results
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={filteredQrCodes.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10))
              setPage(0)
            }}
            rowsPerPageOptions={[25, 50, 100, 250]}
          />
        </TableContainer>
      </Container>

      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  )
}

export default QrExport
